import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { hideGlobalSpinner, showGlobalSpinner } from './globalSpinnerActions';

/**
 * Declarative loader configuration.
 *
 * Register ANY action here to get an automatic full-page loader: when the
 * `trigger` action is dispatched the loader is shown (with `message`), and it
 * is hidden again as soon as any of the `hideOn` actions is dispatched
 * (typically the matching SUCCESS / FAILURE actions).
 *
 * `message` may be a static string or a function of the trigger action, so the
 * loading text can be derived dynamically from the action payload.
 */
export type LoaderActionEntry = {
    /** Loading text shown in the loader card. Static or derived from the action. */
    message?: string | ((action: AnyFSA) => string | undefined);
    /** Action types that dismiss the loader (e.g. the SUCCESS and FAILURE actions). */
    hideOn: string[];
    when?: (state: any, action: AnyFSA) => boolean;
};

export type LoaderActionConfig = Record<string, LoaderActionEntry>;

/**
 * Default action → loader registry.
 *
 * NOTE: Flows that already toggle the global spinner manually (deal/lead
 * submission via activities middleware, dealer-details and dealership
 * fetches) must NOT be added here, or the ref-counted spinner would be
 * double-counted.
 *
 * To add a loader for a new action, add an entry like:
 *
 *   [Actions.CREATE_DEAL]: {
 *       message: 'Creating deal…',
 *       hideOn: [Actions.CREATE_DEAL_SUCCESS, Actions.CREATE_DEAL_FAILURE]
 *   }
 */
export const loaderActionConfig: LoaderActionConfig = {};

const resolveMessage = (entry: LoaderActionEntry, action: AnyFSA): string | undefined =>
    typeof entry.message === 'function' ? entry.message(action) : entry.message;

/**
 * Config-driven loader middleware.
 *
 * Shows/hides the global full-page loader based on the supplied `config`,
 * reusing the existing ref-counted `showGlobalSpinner`/`hideGlobalSpinner`
 * actions so concurrent loaders are handled correctly.
 */
export const loaderMiddleware =
    (config: LoaderActionConfig = loaderActionConfig) =>
    (store: any) =>
    (next: any) =>
    (action: AnyFSA) => {
        next(action);

        // Actions flagged meta.skipGlobalSpinner (e.g. background dealRefId polling) opt out of
        // the loader entirely — skip both show and hide so a concurrent real loader's ref count
        // is never touched.
        if (action.meta?.skipGlobalSpinner) {
            return;
        }

        const triggerEntry = config[action.type];
        if (triggerEntry && (triggerEntry.when?.(store.getState(), action) ?? true)) {
            store.dispatch(showGlobalSpinner(resolveMessage(triggerEntry, action)));
        }

        // Hide the loader for any configured terminal action.
        const isHideAction = Object.values(config).some((entry) => entry.hideOn.includes(action.type));
        if (isHideAction) {
            store.dispatch(hideGlobalSpinner());
        }
    };
